/**
 * Builds the per-courier PWA web app manifest that is injected at runtime once
 * courier data loads (see courierSlice). The courier's own logo becomes the
 * installed app icon, with the static OX icon set as guaranteed fallbacks so
 * installability never depends on the remote (cross-origin) logo being fetchable.
 */

export interface ManifestIcon {
  src: string;
  sizes: string;
  type?: string;
  purpose?: string;
}

export interface WebAppManifest {
  name: string;
  short_name: string;
  description: string;
  start_url: string;
  scope: string;
  display: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser';
  background_color: string;
  theme_color: string;
  orientation: string;
  icons: ManifestIcon[];
  categories: string[];
  shortcuts: Array<{
    name: string;
    short_name: string;
    description: string;
    url: string;
    icons: ManifestIcon[];
  }>;
}

export interface BuildCourierManifestParams {
  courierName: string;
  themeColor: string;
  /** The courier's logo URL (CourierUser/Courier.imageUrl). Optional — falls back to OX icons. */
  imageUrl?: string;
  /** window.location.origin */
  origin: string;
  /**
   * A data URL of a padded, square, maskable version of the courier logo
   * (generated from imageUrl via canvas at runtime). When supplied it becomes the
   * sole maskable icon so Android's adaptive-icon picker uses the courier logo
   * instead of an OX maskable fallback.
   */
  maskableIcon?: string;
}

/** Full-bleed OX fallback icons (purpose 'any'). Always same-origin and present. */
const oxAnyIcons = (origin: string): ManifestIcon[] => [
  { src: `${origin}/icons/icon-192x192.png`, sizes: '192x192', type: 'image/png', purpose: 'any' },
  { src: `${origin}/icons/icon-512x512.png`, sizes: '512x512', type: 'image/png', purpose: 'any' },
];

/** Padded OX maskable fallbacks — only used when we have no courier maskable icon. */
const oxMaskableIcons = (origin: string): ManifestIcon[] => [
  { src: `${origin}/icons/icon-192x192-maskable.png`, sizes: '192x192', type: 'image/png', purpose: 'maskable' },
  { src: `${origin}/icons/icon-512x512-maskable.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
];

export function buildCourierManifest({
  courierName,
  themeColor,
  imageUrl,
  origin,
  maskableIcon,
}: BuildCourierManifestParams): WebAppManifest {
  const icons: ManifestIcon[] = [];

  // Maskable courier icon first — this is the icon Android uses for the home
  // screen (adaptive icon). It must be the only maskable entry, otherwise an OX
  // maskable fallback could be chosen instead of the courier logo.
  if (maskableIcon) {
    icons.push({ src: maskableIcon, sizes: '512x512', type: 'image/png', purpose: 'maskable' });
  }

  // Full-bleed courier logo (purpose 'any').
  // - type omitted: the logo may be jpg/png/webp; declaring a wrong mime gets it rejected.
  if (imageUrl) {
    icons.push({ src: imageUrl, sizes: '512x512', purpose: 'any' });
  }

  // Static OX fallbacks so the app is installable even before courier data loads
  // or if the courier logo fails to fetch. Only add OX maskable icons when we
  // don't already have a courier maskable icon (which would outcompete it).
  icons.push(...oxAnyIcons(origin));
  if (!maskableIcon) {
    icons.push(...oxMaskableIcons(origin));
  }

  const shortcutIcon: ManifestIcon[] = [
    { src: `${origin}/icons/icon-96x96.png`, sizes: '96x96', type: 'image/png' },
  ];

  return {
    name: `${courierName} - Admin`,
    short_name: courierName,
    description: `${courierName} Admin Dashboard - Manage your courier business efficiently`,
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: 'standalone',
    background_color: '#ffffff',
    theme_color: themeColor,
    orientation: 'any',
    icons,
    categories: ['business', 'productivity'],
    shortcuts: [
      { name: 'Dashboard', short_name: 'Dashboard', description: 'Go to Dashboard', url: '/dashboard', icons: shortcutIcon },
      { name: 'Packages', short_name: 'Packages', description: 'View Packages', url: '/packages', icons: shortcutIcon },
      { name: 'Customers', short_name: 'Customers', description: 'View Customers', url: '/customers', icons: shortcutIcon },
    ],
  };
}
